import type Fuse from "fuse.js";
import { type FC, type MutableRefObject, useRef } from "react";

import { InputNumber } from "@moment/ui";

import { useEditableValue } from "../useEditableValue";
import { TableCellValueInputClickMask } from "./TableCellValueInputClickMask";
import { type TableCellValueCommonProps } from "./types";
import { markHighlights } from "./utils";

export interface TableCellValueInputNumberProps extends TableCellValueCommonProps {
	highlightIndices?: Fuse.FuseResultMatch["indices"];
	renderAsRowDetail?: boolean;
	cellValueRef?: MutableRefObject<HTMLDivElement>;
}

export const TableCellValueInputNumber: FC<TableCellValueInputNumberProps> = ({
	value: inputValue,
	defaultValue: defaultInputValue,
	highlightIndices,
	onChange,
	renderAsRowDetail,
	cellValueRef,
	isEditable = true,
	header,
}) => {
	const inputRef = useRef<HTMLInputElement>(null);
	const {
		value,
		isControlled,
		isEditing,
		handleEditButtonClick,
		handleBlur,
		handleChange,
		handleKeyDown,
	} = useEditableValue<number>(
		"number",
		isEditable,
		inputValue,
		defaultInputValue,
		onChange,
		inputRef
	);

	const staticContent = <>{markHighlights(value, highlightIndices)}</>;

	if (!isEditable) {
		return staticContent;
	}

	const input = (
		<InputNumber
			aria-label={header}
			defaultValue={defaultInputValue}
			value={isControlled ? value : undefined}
			ref={inputRef}
			size="sm"
			fullWidth
			noChrome={!renderAsRowDetail}
			onBlur={handleBlur}
			onKeyDown={handleKeyDown}
			onChange={(value) => handleChange(value)}
		/>
	);

	if (renderAsRowDetail) {
		return input;
	}

	const editableContent = isEditing ? (
		input
	) : (
		<TableCellValueInputClickMask
			onDoubleClick={handleEditButtonClick}
			isEditing={isEditing}
			cellValueRef={cellValueRef}
		>
			{staticContent}
		</TableCellValueInputClickMask>
	);

	return isEditable ? editableContent : staticContent;
};
